import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';
import io from 'socket.io-client';

const ChatContext = createContext(null);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};

export const ChatProvider = ({ children }) => {
  const { makeAuthenticatedRequest, isAuthenticated, customer } = useAuth();
  const [unread, setUnread] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [messagesByConv, setMessagesByConv] = useState({});
  const [socket, setSocket] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [unreadByConversation, setUnreadByConversation] = useState({});
  const openConversationsRef = useRef({}); // conversationId -> boolean (open/visible)
  const pollingRef = useRef(null);
  const isLoadingConversationsRef = useRef(false);
  const lastLoadedConversationsAtRef = useRef(0);

  // Initialize socket connection
  useEffect(() => {
    if (!isAuthenticated || !customer?.id) return;

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const SOCKET_URL = API_URL.replace('/api', '');
    const newSocket = io(SOCKET_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('Connected to chat server');
      newSocket.emit('join-customer-room', customer.id);
    });

    newSocket.on('new_message', (message) => {
      // Normalize message format for display
      const normalizedMessage = {
        ...message,
        message_text: message.message_text || message.messageText,
        sent_at: message.sent_at || message.sentAt,
        sender_type: message.sender_type || message.senderType
      };

      const conversationId = message.conversationId;

      setMessagesByConv(prev => ({
        ...prev,
        [conversationId]: [...(prev[conversationId] || []), normalizedMessage]
      }));

      // If message is from admin and the conversation is not currently open, mark unread and notify
      const isFromAdmin = (normalizedMessage.sender_type === 'admin');
      const isOpen = !!openConversationsRef.current[conversationId];
      if (isFromAdmin && !isOpen) {
        setUnreadByConversation(prev => ({
          ...prev,
          [conversationId]: (prev[conversationId] || 0) + 1
        }));

        // Toast notification
        toast((t) => (
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
            </div>
            <div>
              <div className="font-semibold text-gray-900">New message from support</div>
              <div className="text-sm text-gray-600">Tap to open chat</div>
            </div>
          </div>
        ), { 
          id: `chat-${conversationId}`, 
          duration: 5000,
          position: 'top-right'
        });

        // Browser notification if permitted
        try {
          if (typeof window !== 'undefined' && 'Notification' in window) {
            if (Notification.permission === 'granted') {
              new Notification('New support message', { body: normalizedMessage.message_text });
            } else if (Notification.permission !== 'denied') {
              Notification.requestPermission();
            }
          }
        } catch (_) {}
      }
    });

    newSocket.on('conversation-updated', (conversation) => {
      setConversations(prev => 
        prev.map(conv => 
          conv.id === conversation.id ? { ...conv, ...conversation } : conv
        )
      );
    });

    newSocket.on('unread-count-updated', ({ unreadCount }) => {
      setUnread(unreadCount);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, [isAuthenticated, customer?.id]);

  const loadConversations = async () => {
    const now = Date.now();
    if (isLoadingConversationsRef.current) return;
    // Throttle to avoid rapid repeat calls
    if (now - lastLoadedConversationsAtRef.current < 4000) return;
    isLoadingConversationsRef.current = true;
    try {
      const data = await makeAuthenticatedRequest('/chat/conversations');
      if (data.success) setConversations(data.data || []);
      lastLoadedConversationsAtRef.current = Date.now();
    } catch (_) {
    } finally {
      isLoadingConversationsRef.current = false;
    }
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    // Initial bootstrap: conversations, unread, jobs
    const bootstrap = async () => {
      try {
        const data = await makeAuthenticatedRequest('/chat/bootstrap');
        if (data.success) {
          setConversations(data.data.conversations || []);
          setUnread(data.data.unread || 0);
          setJobs(data.data.jobs || []);
        }
      } catch (_) {}
    };
    bootstrap();
    return () => pollingRef.current && clearInterval(pollingRef.current);
  }, [isAuthenticated, makeAuthenticatedRequest]);

  // Auto-refresh conversations periodically
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const interval = setInterval(() => {
      loadConversations();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(interval);
  }, [isAuthenticated, loadConversations]);

  const loadMessages = async (conversationId) => {
    try {
      const data = await makeAuthenticatedRequest(`/chat/conversations/${conversationId}/messages`);
      if (data.success) setMessagesByConv(prev => ({ ...prev, [conversationId]: data.data }));
      // Clear local unread when explicitly loading messages (assumes user viewed)
      setUnreadByConversation(prev => ({ ...prev, [conversationId]: 0 }));
    } catch (_) {}
  };

  const sendMessage = async (conversationId, message) => {
    const tempId = `temp-${Date.now()}`;
    
    // Immediately add message to UI (optimistic update)
    const tempMessage = {
      id: tempId,
      conversationId: conversationId,
      sender_type: 'customer',
      message_text: message,
      messageText: message,
      sent_at: new Date().toISOString(),
      sentAt: new Date().toISOString(),
      isTemporary: true
    };
    
    setMessagesByConv(prev => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] || []), tempMessage]
    }));
    
    try {
      const res = await makeAuthenticatedRequest(`/chat/conversations/${conversationId}/messages`, {
        method: 'POST',
        body: JSON.stringify({ message })
      });
      
      if (res.success) {
        // Replace temp with finalized message (socket does not echo to customer)
        setMessagesByConv(prev => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).map(m => m.id === tempId ? {
            id: res.data.id,
            conversationId,
            sender_type: 'customer',
            message_text: message,
            sent_at: new Date().toISOString()
          } : m)
        }));
      } else {
        // Remove temp message on failure
        setMessagesByConv(prev => ({
          ...prev,
          [conversationId]: (prev[conversationId] || []).filter(m => m.id !== tempId)
        }));
      }
      
      return res;
    } catch (error) {
      // Remove temp message on error
      setMessagesByConv(prev => ({
        ...prev,
        [conversationId]: (prev[conversationId] || []).filter(m => m.id !== tempId)
      }));
      throw error;
    }
  };

  const startConversation = async (subject, jobId) => {
    const res = await makeAuthenticatedRequest(`/chat/conversations`, {
      method: 'POST',
      body: JSON.stringify({ subject, jobId })
    });
    return res;
  };

  const markAsRead = async (messageId) => {
    try {
      await makeAuthenticatedRequest(`/chat/messages/${messageId}/read`, {
        method: 'PUT'
      });
    } catch (_) {}
  };

  const reloadJobs = async () => {
    try {
      const data = await makeAuthenticatedRequest('/chat/eligible-jobs');
      if (data.success) setJobs(data.data || []);
    } catch (_) {}
  };

  const value = useMemo(() => ({ 
    unread, 
    conversations, 
    messagesByConv, 
    jobs,
    unreadByConversation,
    loadConversations, 
    loadMessages, 
    sendMessage, 
    markAsRead,
    startConversation,
    reloadJobs,
    socket,
    // Expose helpers to toggle conversation open/closed state for accurate unread
    setConversationOpen: (conversationId, isOpen) => {
      if (!conversationId) return;
      openConversationsRef.current[conversationId] = isOpen;
      if (isOpen) {
        setUnreadByConversation(prev => ({ ...prev, [conversationId]: 0 }));
      }
    }
  }), [unread, conversations, messagesByConv, socket, unreadByConversation]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};


